/**
 * Page Cache Manager
 * Extends existing cache layer for page-specific background caching
 */

import { cacheGet, cacheSet, cacheDelete, getCacheStats } from './cacheLayer.js';

class PageCacheManager {
  constructor() {
    this.pageRegistry = new Map();
    this.updateQueue = [];
    this.isProcessing = false;
    this.isPaused = false;
    this.performanceMonitor = {
      cpuUsage: 0,
      memoryUsage: 0,
      userActivity: true
    };
  }

  /**
   * Register a page for background caching
   */
  registerPage(pageName, dependencies, calculationFn, priority = 'medium') {
    this.pageRegistry.set(pageName, {
      dependencies,
      calculationFn,
      priority,
      lastUpdated: null,
      status: 'fresh', // fresh, stale, updating, error
      cacheKey: `page_${pageName}`,
      ttl: 30 * 60 * 1000 // 30 minutes
    });
  }

  /**
   * Get page cache using existing cache system
   */
  getPageCache(pageName, businessId) {
    const pageConfig = this.pageRegistry.get(pageName);
    if (!pageConfig) return null;

    const cacheKey = `${pageConfig.cacheKey}_${businessId}`;
    const cached = cacheGet(cacheKey);
    
    if (cached && this.isCacheFresh(cached, pageConfig)) {
      return cached;
    }
    
    return null;
  }

  /**
   * Set page cache using existing cache system
   */
  setPageCache(pageName, businessId, data) {
    const pageConfig = this.pageRegistry.get(pageName);
    if (!pageConfig) return false;

    const cacheKey = `${pageConfig.cacheKey}_${businessId}`;
    const cacheData = {
      ...data,
      lastUpdated: Date.now(),
      pageName,
      businessId,
      dataVersion: this.getDataVersion()
    };

    return cacheSet(cacheKey, cacheData, pageConfig.ttl);
  }

  /**
   * Check if cache is fresh
   */
  isCacheFresh(cached, pageConfig) {
    if (!cached || !cached.lastUpdated) return false;
    
    const age = Date.now() - cached.lastUpdated;
    return age < pageConfig.ttl;
  }

  /**
   * Get current data version for cache validation
   */
  getDataVersion() {
    return Date.now().toString(36);
  }

  /**
   * Queue background update for a page
   */
  queueBackgroundUpdate(pageName, changeEvent) {
    const pageConfig = this.pageRegistry.get(pageName);
    if (!pageConfig) return;

    // Check if already queued
    const existingUpdate = this.updateQueue.find(update => update.pageName === pageName);
    if (existingUpdate) {
      existingUpdate.changeEvent = changeEvent;
      existingUpdate.timestamp = Date.now();
      return;
    }

    // Add to queue
    this.updateQueue.push({
      pageName,
      changeEvent,
      timestamp: Date.now(),
      priority: pageConfig.priority,
      status: 'queued'
    });

    // Sort by priority
    this.updateQueue.sort((a, b) => {
      const priorities = { high: 1, medium: 2, low: 3 };
      return priorities[a.priority] - priorities[b.priority];
    });

    // Start processing if not already running
    if (!this.isProcessing) {
      this.processQueue();
    }
  }

  /**
   * Process background update queue
   */
  async processQueue() {
    if (this.isProcessing || this.isPaused || this.updateQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    try {
      while (this.updateQueue.length > 0 && !this.isPaused) {
        // Check if we should pause
        if (this.shouldPauseBackgroundWork()) {
          this.isPaused = true;
          break;
        }

        const update = this.updateQueue.shift();
        await this.processBackgroundUpdate(update);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Process individual background update
   */
  async processBackgroundUpdate(update) {
    const { pageName, changeEvent } = update;
    const pageConfig = this.pageRegistry.get(pageName);
    
    if (!pageConfig) return;

    try {
      // Update status
      pageConfig.status = 'updating';
      
      // Get current data for calculation
      const currentData = await this.getCurrentData(pageConfig.dependencies);
      
      // Calculate new data using requestIdleCallback
      const freshData = await this.calculateInBackground(
        pageConfig.calculationFn,
        currentData
      );

      // Update cache
      this.setPageCache(pageName, changeEvent.businessId || 'default', freshData);
      
      // Update status
      pageConfig.status = 'fresh';
      pageConfig.lastUpdated = Date.now();

      // Notify pages of fresh data
      this.notifyPagesOfUpdate(pageName, freshData);

    } catch (error) {
      console.error(`Background update failed for ${pageName}:`, error);
      pageConfig.status = 'error';
    }
  }

  /**
   * Calculate data in background using requestIdleCallback
   */
  calculateInBackground(calculationFn, data) {
    return new Promise((resolve, reject) => {
      const calculate = async () => {
        try {
          const result = await calculationFn(data);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      if (window.requestIdleCallback) {
        window.requestIdleCallback(calculate, { timeout: 5000 });
      } else {
        setTimeout(calculate, 0);
      }
    });
  }

  /**
   * Get current data for dependencies
   */
  async getCurrentData(dependencies) {
    // This would integrate with your existing data context
    // For now, return empty object - will be implemented in integration
    return {};
  }

  /**
   * Notify pages of fresh data
   */
  notifyPagesOfUpdate(pageName, data) {
    window.dispatchEvent(new CustomEvent('backgroundUpdateComplete', {
      detail: { pageName, data, timestamp: Date.now() }
    }));
  }

  /**
   * Check if background work should be paused
   */
  shouldPauseBackgroundWork() {
    return this.performanceMonitor.cpuUsage > 80 || 
           this.performanceMonitor.memoryUsage > 90 || 
           this.performanceMonitor.userActivity;
  }

  /**
   * Pause background processing
   */
  pause() {
    this.isPaused = true;
  }

  /**
   * Resume background processing
   */
  resume() {
    this.isPaused = false;
    if (this.updateQueue.length > 0) {
      this.processQueue();
    }
  }

  /**
   * Update performance metrics
   */
  updatePerformanceMetrics(cpuUsage, memoryUsage, userActivity) {
    this.performanceMonitor = { cpuUsage, memoryUsage, userActivity };
  }

  /**
   * Get cache statistics
   */
  getCacheStatistics() {
    const stats = getCacheStats();
    return {
      ...stats,
      registeredPages: this.pageRegistry.size,
      queuedUpdates: this.updateQueue.length,
      isProcessing: this.isProcessing,
      isPaused: this.isPaused
    };
  }

  /**
   * Clear all page caches
   */
  clearAllPageCaches() {
    for (const [pageName, config] of this.pageRegistry) {
      cacheDelete(config.cacheKey);
    }
  }
}

// Create singleton instance
const pageCacheManager = new PageCacheManager();

export default pageCacheManager;
